import { useState, useEffect, useRef } from 'react';
import styles from './sidemenu.module.css';
import defaultMenu from "../../../config/sideMenuData";
import { useNavigate, useLocation } from "react-router-dom"; // For internal navigation
import { motion, AnimatePresence } from "framer-motion";
import { Icons } from '../../../code/IconHandler';
import { getBackgroundColor } from '../../../code/ColorHandler';
import { forceReload } from '../FussballDeWidget/FussballDeWidget';
import { getFooterBackground } from '../../../code/ColorHandler';

function findMenuPathByUrl(menuItems, url, path = []) {
    for (const item of menuItems) {
        if (decodeURIComponent(item.link) === url) {
            // If item has no subcontent, return parent path (exclude this item)
            if (!item.subcontent || item.subcontent.length === 0) {
                return path.length > 0 ? path : [item];
                // if no parent (path empty), fallback to current item itself
            }
            return [...path, item];
        }
        if (item.subcontent) {
            const subPath = findMenuPathByUrl(item.subcontent, url, [...path, item]);
            if (subPath) return subPath;
        }
    }
    return null;
}
function SideMenu({ isMenuOpen, setIsMenuOpen }) {
    const menuContentRef = useRef(null);
    const [menuHistory, setMenuHistory] = useState([]);
    const [currentMenu, setCurrentMenu] = useState({ title: "default", items: defaultMenu, link: "/" });
    const navigate = useNavigate();
    const location = useLocation();
    const menuVariants = {
        enter: { x: "100%", opacity: 0 },
        center: { x: 0, opacity: 1 },
        exit: { x: "-100%", opacity: 0 }
    };
    const [hoverColor, setHoverColor] = useState('');

    useEffect(() => {
        const color = getFooterBackground(location);
        setHoverColor(color);
    }, [location]);
    const handleTitleClick = (currentMenu) => {
        const val = currentMenu['link']
        if (forceReload(val)) {
            window.location.href = val;
        } else {
            navigate(val)
        }
        navigate(val)

        setIsMenuOpen(false);
    }
    const handleItemClick = (item) => {
        if (item.subcontent) {
            // Update the menu stack and current menu
            setMenuHistory([...menuHistory, currentMenu]);
            setCurrentMenu({ title: item.name, items: item.subcontent, link: item.link });

            // If there's a link, and you want to reflect something on the background:
            if (item.link) {
                if (item.link.startsWith("https://") || item.link.startsWith("http://")) {
                    window.open(item.link, "_blank");
                } else {
                    // Don't close menu but still navigate silently in background
                    if (!forceReload(item.link)) {
                        navigate(item.link);
                    }
                    // Don't close the menu
                }
            }
        } else if (item.link) {
            if (item.link.startsWith("https://") || item.link.startsWith("http://")) {
                window.open(item.link, "_blank");
            } else {
                if (forceReload(item.link)) {
                    window.location.href = item.link;
                } else {
                    navigate(item.link);
                }
                setIsMenuOpen(false);
            }
        }
    };

    const goBack = () => {
        if (menuHistory.length > 0) {
            const previousMenu = menuHistory[menuHistory.length - 1];
            setCurrentMenu(previousMenu);
            setMenuHistory(menuHistory.slice(0, -1));

            // Navigate in the background if link exists
            if (previousMenu.link) {

                if (!forceReload(previousMenu.link)) {
                    navigate(previousMenu.link);
                } else {
                    window.location.href = previousMenu.link;
                }

            }
        }
    };



    useEffect(() => {
        const decodedUrlPath = decodeURIComponent(location.pathname);
        const menuPath = findMenuPathByUrl(defaultMenu, decodedUrlPath);

        if (menuPath) {
            let history = [];
            let current;

            const lastItem = menuPath[menuPath.length - 1];

            // If lastItem has no subcontent or empty subcontent, show parent menu if exists
            if (!lastItem.subcontent || lastItem.subcontent.length === 0) {
                if (menuPath.length > 1) {
                    // parent is second last in path
                    const parentItem = menuPath[menuPath.length - 2];

                    // history is everything before parent
                    history = menuPath.slice(0, menuPath.length - 2).map(item => ({
                        title: item.name,
                        items: item.subcontent || [],
                        link: item.link || "/",
                    }));

                    // Ensure default root is at start of history if not already
                    if (history.length === 0 || history[0].title !== "default") {
                        history.unshift({
                            title: "default",
                            items: defaultMenu,
                            link: "/",
                        });
                    }

                    current = {
                        title: parentItem.name,
                        items: parentItem.subcontent || [],
                        link: parentItem.link || "/",
                    };
                } else {
                    // Only one item in path and no subcontent: show default menu
                    history = [];
                    current = {
                        title: "default",
                        items: defaultMenu,
                        link: "/",
                    };
                }
            } else {
                // last item has subcontent: normal behavior
                history = menuPath.slice(0, menuPath.length - 1).map(item => ({
                    title: item.name,
                    items: item.subcontent || [],
                    link: item.link || "/",
                }));

                // Ensure default root is at start of history if not already
                if (history.length === 0 || history[0].title !== "default") {
                    history.unshift({
                        title: "default",
                        items: defaultMenu,
                        link: "/",
                    });
                }

                current = {
                    title: lastItem.name,
                    items: lastItem.subcontent || [],
                    link: lastItem.link || "/",
                };
            }

            setMenuHistory(history);
            setCurrentMenu(current);
        } else {
            // No matching path found, reset to default
            setMenuHistory([]);
            setCurrentMenu({ title: "default", items: defaultMenu, link: "/" });
        }
    }, [location.pathname]);
    return (
        <>
            {/* Background overlay when menu is open */}
            {isMenuOpen && <div className={styles.menuOverlay} onClick={() => setIsMenuOpen(false)}></div>}

            {/* Side Menu */}
            <div className={`${styles.sideMenu} ${isMenuOpen ? styles.open : ""}`} style={{ background: getBackgroundColor(location) }}>
                <Icons.X
                    onClick={() => setIsMenuOpen(false)}
                    className={styles.x}
                />
                <div className={styles.menuTitle}>
                    <img className={styles.logo} src={`${process.env.PUBLIC_URL}/images/logo.png`}
                        alt="TuS05-Logo" />
                    <p>Menü</p>

                </div>
                <nav className={styles.contentSection}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentMenu.title}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            variants={menuVariants}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className={styles.menuContent}
                            ref={menuContentRef}
                        >
                            {currentMenu.title !== "default" && (
                                <div className={styles.contentTitle}>
                                    <Icons.ArrowRight className={`${styles.arrow} ${styles.st}`}
                                        style={{ '--hover-color': hoverColor }}
                                        onClick={goBack} />
                                    {/* <svg className={styles.st}  data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88"><title>greater-than</title><polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" /></svg> */}
                                    <div>
                                        <p className={styles.title}
                                            style={{ '--hover-color': hoverColor }}
                                            onClick={() => handleTitleClick(currentMenu)}>{currentMenu.title}</p>
                                    </div>
                                </div>
                            )}
                            {/* 
                            {showTopIndicator && (
                                <div className={`${styles.scrollIndicator} ${styles.top} ${styles.showIndicator}`} onClick={scrollToTop}>
                                    <svg className={styles.pointUp} data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88"><title>greater-than</title><polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" /></svg>
                                </div>
                            )} */}


                            {currentMenu.items.map((item, index) => (
                                <div key={index} className={styles.singleItemDiv} onClick={() => handleItemClick(item)}>
                                    <div className={styles.textArrowDiv}
                                        style={{ '--hover-color': hoverColor }}
                                    >
                                        <p className={styles.menuItem}>{item.name}</p>
                                        <Icons.ArrowRight className={styles.arrow} />
                                    </div>
                                </div>
                            ))}

                            {/* {showBottomIndicator && (
                                <div className={`${styles.scrollIndicator} ${styles.bottom} ${styles.showIndicator}`} onClick={scrollToBottom}>
                                    <svg className={styles.pointDown} data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88"><title>greater-than</title><polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" /></svg>
                                </div>
                            )} */}
                        </motion.div>
                    </AnimatePresence>
                </nav >
            </div>
        </>
    );
}
export default SideMenu;